/** Durable Machine topology facet. Paints reported relationships; never infers packet health. */
import { useMemo } from "react";
import { html, M, Alert, SOFT, cmcPlainOf } from "./ui.js";
import { path, useApi } from "./api.js";
import { edgeDevicesOf } from "./edgeos-live.js";
import { machineHubRowOf } from "./machine-deck.js";

const POSITIONS = [
  [13, 22, 16], [76, 17, -8], [82, 64, 18],
  [18, 72, -12], [49, 9, 8], [50, 82, -5],
];

function safePath(op, opts) {
  try {
    return path(op, opts);
  } catch {
    if (op === "get__devices") return "/devices?limit=80";
    if (op === "get__devices_p") {
      return "/devices/" + encodeURIComponent(String(opts && opts.pathParams && opts.pathParams[0] || ""));
    }
    return "";
  }
}

function relatedTo(device, register) {
  if (!device) return [];
  return register.filter((row) => row.id !== device.id && (
    (device.group && row.group === device.group)
    || (device.site && row.site === device.site)
    || (device.zone && row.zone === device.zone)
  )).slice(0, POSITIONS.length);
}

export default function MachineTopologyWorkspace({ deviceId }) {
  const did = String(deviceId || "").trim();
  const detail = useApi(did ? safePath("get__devices_p", { pathParams: [did] }) : "");
  const register = useApi(safePath("get__devices", { query: { limit: 80 } }));
  const rows = useMemo(
    () => edgeDevicesOf(register.data).map(machineHubRowOf),
    [register.data],
  );
  const current = rows.find((row) => row.id === did) || {
    id: did,
    label: String(detail.data && (detail.data.display_label || detail.data.map_label) || did),
    online: !!(detail.data && detail.data.online),
    group: String(detail.data && (detail.data.notification_group || detail.data.group) || ""),
    site: String(detail.data && (detail.data.site || detail.data.site_id) || ""),
    zone: String(detail.data && detail.data.zone || ""),
  };
  const peers = relatedTo(current, rows);

  return html`<section className="machine-topology machine-topology--workspace"
      data-live=${current.online ? "1" : "0"} data-cmc-machine-workspace="topology">
    <div className="machine-v2__section-head"><div><span>Relationship topology</span>
      <small>Reported Site, Zone, and Group relationships. This is not a packet trace or proof of reachability.</small></div>
      <em>${peers.length} related machines</em>
    </div>
    ${detail.error || register.error ? html`<${Alert} tone="warn" title="Topology is partial"
      say=${cmcPlainOf(detail.error || register.error)}/>` : null}
    <div className="machine-topology__scene">
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
        ${peers.map((peer, i) => html`<line key=${peer.id} x1="50" y1="50"
          x2=${POSITIONS[i][0]} y2=${POSITIONS[i][1]}
          data-live=${peer.online && current.online ? "1" : "0"}/>`)}
      </svg>
      <div className="machine-topology__core" data-live=${current.online ? "1" : "0"}>
        <i/><b>${current.label || did}</b><small>${current.online ? "Reported connected" : "Not answering"}</small>
      </div>
      ${peers.map((peer, i) => html`<${M.div} key=${peer.id}
        className="machine-topology__node" data-live=${peer.online ? "1" : "0"}
        style=${{"--x":POSITIONS[i][0]+"%","--y":POSITIONS[i][1]+"%","--z":POSITIONS[i][2]+"px"}}
        initial=${{opacity:0,scale:.8}} animate=${{opacity:1,scale:1}}
        transition=${{...SOFT,delay:i*.045}}>
        <i/><b>${peer.label || peer.id}</b><small>${peer.group || peer.zone || peer.site || "related"}</small>
      <//>`)}
      ${!peers.length ? html`<p className="machine-topology__empty">
        No related machines are reported in this Site, Zone, or Group.</p>` : null}
    </div>
    <div className="machine-topology__legend"><span data-live="1"><i/>Reported active</span>
      <span><i/>Relationship only</span><em>Open Diagnostics for freshness and recovery</em></div>
  </section>`;
}
